/**
 * BAYC TCG — authoritative rules engine.
 *
 * This is the ONLY place game rules are enforced. The client never decides legality; it
 * renders what the server sends and asks the server to make moves.
 *
 * Board indices:
 *   0 1 2
 *   3 4 5
 *   6 7 8
 */
import { BY_ID } from "./cards.js";

/** Cards in an opening hand. Changing this here changes it everywhere: the
 *  server enforces it, the client reads it from /api/cards, and the Oracle's
 *  deck drafting and opponent model are both derived from DEFAULT_CURVE. */
export const HAND_SIZE = 13;

/** The rarity split the sweeps favour at HAND_SIZE. C/U/R/P/L.
 *  At ten cards the best hand was 5/2/1/1/1. At thirteen the Common count does
 *  NOT grow — a 28-way sweep and then an 8-way round robin (14,000 games per
 *  curve) both land on 5/3/2/2/1, i.e. all three extra cards go into upgrade
 *  fuel. You only ever place about nine cards, so Commons past what you need
 *  to claim squares are dead weight. */
export const DEFAULT_CURVE = [5, 3, 2, 2, 1];

export const RARITIES = ["Common", "Uncommon", "Rare", "Prestige", "Legendary"];

/** ADVANCED MODE — the four families form one dominance cycle: each beats
 *  exactly one other, loses to exactly one, and is neutral against the fourth.
 *  It only ever decides a TIE on a touching edge; it never lets a lower number
 *  beat a higher one. */
export const FAMILY_BEATS = {
  CLUB: "MUTANT",
  MUTANT: "COSMIC",
  COSMIC: "ARCANA",
  ARCANA: "CLUB",
};
export const dominates = (atkFamily, defFamily) =>
  FAMILY_BEATS[atkFamily] === defFamily;

/** With the ante on, a player picks one more card than they will play: one is
 *  pulled at random before the first move. */
export const ANTE_EXTRA = 1;
export const deckSizeFor = (rules) =>
  HAND_SIZE + (rules && rules.ante ? ANTE_EXTRA : 0);
/** The rules a room is played under. Both off is the base game. */
export const defaultRules = () => ({ ante: false, advanced: false });
export const RI = Object.fromEntries(RARITIES.map((r, i) => [r, i]));

/** The rarities are also numbered: the ladder is Level I -> V, and the rarity
 *  name is the subtype of its level. This is presentation only — the rarity
 *  name stays the identifier everywhere in the rules, the card data and the
 *  wire format, so nothing about saved decks or the card file changes.
 *  The client reads LEVELS from /api/cards rather than keeping its own copy. */
export const ROMAN = ["I", "II", "III", "IV", "V"];
export const LEVELS = Object.fromEntries(RARITIES.map((r, i) => [r, ROMAN[i]]));
/** "Level III" — the name of the tier on its own. */
export const levelName = (r) => `Level ${LEVELS[r]}`;
/** "Level III · Rare" — the tier with its subtype, for labels with room. */
export const rarityLabel = (r) => `Level ${LEVELS[r]} · ${r}`;

export const NB = [];
for (let i = 0; i < 9; i++) {
  const r = Math.floor(i / 3),
    c = i % 3,
    n = [];
  if (r > 0) n.push([i - 3, "T", "B"]);
  if (r < 2) n.push([i + 3, "B", "T"]);
  if (c > 0) n.push([i - 1, "L", "R"]);
  if (c < 2) n.push([i + 1, "R", "L"]);
  NB.push(n);
}
export const EXPOSURE = NB.map((n) => n.length);
export const CORNERS = new Set([0, 2, 6, 8]);
export const CENTRE = 4;
export const SIDES = new Set([1, 3, 5, 7]);

export const tier = (c) => RI[c.r];

export function newState(
  hand0,
  hand1,
  first = 0,
  maxRocks = [1, 1],
  opts = {}
) {
  const rules = { ...defaultRules(), ...(opts.rules || {}) };
  const hands = [hand0.slice(), hand1.slice()];
  // ANTE: pull one card at random out of each hand before play. It is kept on
  // the state so the end of the game can reveal it, and it is never sent to the
  // opponent while the game is running.
  const ante = [null, null];
  if (rules.ante) {
    const rnd = opts.rand || Math.random;
    for (let p = 0; p < 2; p++) {
      if (hands[p].length)
        ante[p] = hands[p].splice(Math.floor(rnd() * hands[p].length), 1)[0];
    }
  }
  return {
    board: Array.from({ length: 9 }, () => ({
      stack: [],
      owner: null,
      down: false,
    })),
    hands,
    toMove: first,
    first,
    rocks: [0, 0],
    maxRocks: maxRocks.slice(),
    passes: 0,
    log: [],
    rules,
    ante,
  };
}

export const boardFull = (s) => s.board.every((x) => x.stack.length > 0);
export const isOver = (s) => boardFull(s) || s.passes >= 2;
export const topOf = (sl) => sl.stack[sl.stack.length - 1];
/** A face-down rock counts as a Common no matter what it really is. */
export const effTier = (sl) => (sl.down ? 0 : tier(topOf(sl)));

export function legalMoves(s, player) {
  const p = player == null ? s.toMove : player;
  const hand = s.hands[p],
    out = [];
  const empty = [];
  for (let i = 0; i < 9; i++) if (!s.board[i].stack.length) empty.push(i);

  for (let hi = 0; hi < hand.length; hi++) {
    const card = hand[hi];
    if (tier(card) === 0)
      for (const i of empty) out.push({ kind: "place", hi, slot: i });
    if (s.rocks[p] < s.maxRocks[p])
      for (const i of empty) out.push({ kind: "rock", hi, slot: i });
    for (let i = 0; i < 9; i++) {
      const sl = s.board[i];
      if (!sl.stack.length || sl.owner !== p) continue;
      if (tier(card) === effTier(sl) + 1)
        out.push({ kind: "upgrade", hi, slot: i });
    }
  }
  if (!out.length) out.push({ kind: "pass", hi: -1, slot: -1 });
  return out;
}

function resolveCaptures(s, index, player) {
  const atk = topOf(s.board[index]),
    taken = [];
  const adv = !!(s.rules && s.rules.advanced);
  for (const [ni, myE, thE] of NB[index]) {
    const n = s.board[ni];
    if (!n.stack.length || n.owner === player || n.down) continue; // rocks are immune
    const def = topOf(n);
    const mine = atk[myE],
      theirs = def[thE];
    // strictly greater always takes; in advanced mode a tie also takes when the
    // attacking card's family dominates the defender's
    if (mine > theirs || (adv && mine === theirs && dominates(atk.f, def.f))) {
      n.owner = player;
      taken.push(ni);
    }
  }
  return taken; // captures never chain
}

export function applyMove(s, mv) {
  const p = s.toMove;
  if (mv.kind === "pass") {
    s.passes++;
    s.log.push({ p, kind: "pass", cap: [], slot: -1, name: "—" });
    s.toMove = 1 - p;
    return [];
  }
  s.passes = 0;
  const card = s.hands[p].splice(mv.hi, 1)[0];
  const sl = s.board[mv.slot];
  let cap = [];
  if (mv.kind === "rock") {
    sl.stack.push(card);
    sl.owner = p;
    sl.down = true;
    s.rocks[p]++;
  } else if (mv.kind === "place") {
    sl.stack.push(card);
    sl.owner = p;
    sl.down = false;
    cap = resolveCaptures(s, mv.slot, p);
  } else if (mv.kind === "upgrade") {
    if (sl.down) s.rocks[p]--; // covering your rock frees it
    sl.stack.push(card);
    sl.owner = p;
    sl.down = false;
    cap = resolveCaptures(s, mv.slot, p);
  } else {
    throw new Error("unknown move kind " + mv.kind);
  }
  s.log.push({
    p,
    kind: mv.kind,
    cardId: card.id,
    name: card.n,
    rarity: card.r,
    slot: mv.slot,
    cap,
  });
  s.toMove = 1 - p;
  return cap;
}

export function control(s) {
  let a = 0,
    b = 0;
  for (const sl of s.board) {
    if (sl.owner === 0) a++;
    else if (sl.owner === 1) b++;
  }
  return [a, b];
}

export function result(s) {
  const [a, b] = control(s);
  return {
    control: [a, b],
    winner: a > b ? 0 : b > a ? 1 : null,
    margin: a - b,
  };
}

/** Is this move legal in this state, for the player to move? */
export function findLegal(s, mv) {
  return (
    legalMoves(s, s.toMove).find(
      (m) => m.kind === mv.kind && m.hi === mv.hi && m.slot === mv.slot
    ) || null
  );
}

// --------------------------------------------------------------- serialising
// Hands and board stacks are stored as card ids so state survives hibernation.

export function serialise(s) {
  return {
    board: s.board.map((x) => ({
      stack: x.stack.map((c) => c.id),
      owner: x.owner,
      down: x.down,
    })),
    hands: s.hands.map((h) => h.map((c) => c.id)),
    toMove: s.toMove,
    first: s.first,
    rocks: s.rocks,
    maxRocks: s.maxRocks,
    passes: s.passes,
    log: s.log,
    rules: s.rules || defaultRules(),
    ante: (s.ante || [null, null]).map((c) => (c ? c.id : null)),
  };
}

export function deserialise(o) {
  return {
    board: o.board.map((x) => ({
      stack: x.stack.map((id) => BY_ID[id]),
      owner: x.owner,
      down: x.down,
    })),
    hands: o.hands.map((h) => h.map((id) => BY_ID[id])),
    toMove: o.toMove,
    first: o.first,
    rocks: o.rocks,
    maxRocks: o.maxRocks,
    passes: o.passes,
    log: o.log,
    rules: { ...defaultRules(), ...(o.rules || {}) },
    ante: (o.ante || [null, null]).map((id) => (id ? BY_ID[id] : null)),
  };
}
